import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DemographicData {
  genderData: { name: string; value: number; color: string; percentage: number }[];
  ageRangeData: { range: string; count: number }[];
  tenureData: { range: string; count: number }[];
  turnoverData: { month: string; hires: number; departures: number }[];
  totalActive: number;
  avgTenure: number;
  avgBusinessExperience: number;
  turnoverRate: number;
  avgSatisfactionRating: number;
  totalComplaints: number;
  pendingComplaints: number;
  avgFatigueLevel: number;
  complaintResolutionData: {
    totalComplaints: number;
    resolvedComplaints: number;
    resolvedWithSatisfaction: number;
    resolvedWithoutSatisfaction: number;
    resolutionRate: number;
    satisfactionRate: number;
    target: number;
  };
}

type FilterType = 'all' | 'guards' | 'admins';

export const useDemographicData = (
  selectedFilter: FilterType,
  selectedTeam: string,
  teams: { id: string; name: string }[]
) => {
  const [data, setData] = useState<DemographicData>({
    genderData: [],
    ageRangeData: [],
    tenureData: [],
    turnoverData: [],
    totalActive: 0,
    avgTenure: 0,
    avgBusinessExperience: 0,
    turnoverRate: 0,
    avgSatisfactionRating: 0,
    totalComplaints: 0,
    pendingComplaints: 0,
    avgFatigueLevel: 0,
    complaintResolutionData: {
      totalComplaints: 0,
      resolvedComplaints: 0,
      resolvedWithSatisfaction: 0,
      resolvedWithoutSatisfaction: 0,
      resolutionRate: 0,
      satisfactionRate: 0,
      target: 80,
    },
  });
  
  const [loading, setLoading] = useState(true);
  const [profileIds, setProfileIds] = useState<string[]>([]);

  const fetchDemographicData = async () => {
    try {
      setLoading(true);
      console.log('Fetching demographic data for filter:', selectedFilter, 'team:', selectedTeam);

      // Fetch active profiles with roles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          date_of_birth,
          hiring_date,
          gender,
          status
        `)
        .eq('status', 'active');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Fetch user roles separately
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        throw rolesError;
      }

      // Fetch team members if team filter is applied
      let teamMembers: string[] = [];
      if (selectedTeam !== 'all') {
        const { data: teamMembersData, error: teamMembersError } = await supabase
          .from('team_members')
          .select('profile_id')
          .eq('team_id', selectedTeam);

        if (teamMembersError) {
          console.error('Error fetching team members:', teamMembersError);
        } else {
          teamMembers = teamMembersData?.map(tm => tm.profile_id) || [];
        }
      }

      // Create a map of user roles for easy lookup
      const roleMap = new Map<string, string[]>();
      userRoles?.forEach(ur => {
        if (!roleMap.has(ur.user_id)) {
          roleMap.set(ur.user_id, []);
        }
        roleMap.get(ur.user_id)?.push(ur.role);
      });

      // Filter profiles based on selected role and team
      const filteredProfiles = profiles?.filter(profile => {
        const roles = roleMap.get(profile.id) || [];
        
        // Role filter
        let roleMatch = true;
        if (selectedFilter === 'guards') {
          roleMatch = roles.includes('guard');
        } else if (selectedFilter === 'admins') {
          roleMatch = roles.includes('admin') || roles.includes('super_admin') || roles.includes('manager');
        }
        
        // Team filter
        let teamMatch = true;
        if (selectedTeam !== 'all') {
          teamMatch = teamMembers.includes(profile.id);
        }
        
        return roleMatch && teamMatch;
      }) || [];

  const processedData = await processProfileData(filteredProfiles);
      setData(processedData);
      setProfileIds(filteredProfiles.map(p => p.id));

    } catch (error) {
      console.error('Error fetching demographic data:', error);
      setData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teams.length > 0 || selectedTeam === 'all') {
      fetchDemographicData();
    }
  }, [selectedFilter, selectedTeam, teams]);

  return { data, loading, refetch: fetchDemographicData, profileIds };
};

const processProfileData = async (filteredProfiles: any[]): Promise<DemographicData> => {
  // Process basic demographic data
  const genderCounts = { male: 0, female: 0, unknown: 0 };
  const ages: number[] = [];
  const tenureDays: number[] = [];
  const profileIds = filteredProfiles.map(p => p.id);

  filteredProfiles.forEach(profile => {
    // Count gender
    if (profile.gender === 'male') genderCounts.male++;
    else if (profile.gender === 'female') genderCounts.female++;
    else genderCounts.unknown++;

    // Calculate age
    if (profile.date_of_birth) {
      const birthDate = new Date(profile.date_of_birth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const adjustedAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      ages.push(adjustedAge);
    }

    // Calculate tenure
    if (profile.hiring_date) {
      const hiringDate = new Date(profile.hiring_date);
      const today = new Date();
      const days = Math.floor((today.getTime() - hiringDate.getTime()) / (1000 * 60 * 60 * 24));
      if (days >= 0) tenureDays.push(days);
    }
  });

  // Fetch additional data
  const avgSatisfactionRating = await fetchLatestSatisfactionRatings(profileIds);
  const { totalComplaints, pendingComplaints } = await fetchComplaintsData(profileIds);
  const complaintResolutionData = await fetchComplaintResolutionData(profileIds);
  const avgFatigueLevel = await fetchLatestFatigueData(profileIds);

  // Build demographic data structure
  return buildDemographicData({
    genderCounts,
    ages,
    tenureDays,
    totalActive: filteredProfiles.length,
    avgSatisfactionRating,
    totalComplaints,
    pendingComplaints,
    complaintResolutionData,
    avgFatigueLevel
  });
};

const fetchLatestSatisfactionRatings = async (profileIds: string[]): Promise<number> => {
  if (profileIds.length === 0) return 0;

  const { data: satisfactionData, error: satisfactionError } = await supabase
    .from('guard_satisfaction_ratings')
    .select('guard_id, satisfaction_score, rating_date, created_at')
    .in('guard_id', profileIds)
    .gte('rating_date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    .order('guard_id')
    .order('rating_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (!satisfactionError && satisfactionData && satisfactionData.length > 0) {
    const latestRatings = new Map();
    satisfactionData.forEach(rating => {
      if (!latestRatings.has(rating.guard_id)) {
        latestRatings.set(rating.guard_id, rating);
      }
    });
    
    const ratings = Array.from(latestRatings.values());
    const totalSatisfaction = ratings.reduce((sum, item: any) => sum + item.satisfaction_score, 0);
    return Math.round((totalSatisfaction / ratings.length) * 10) / 10;
  }
  
  return 0;
};

const fetchComplaintsData = async (profileIds: string[]) => {
  if (profileIds.length === 0) return { totalComplaints: 0, pendingComplaints: 0 };

  const { data: complaintsData, error: complaintsError } = await supabase
    .from('guard_complaints')
    .select('id, status')
    .in('guard_id', profileIds)
    .gte('complaint_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

  if (!complaintsError && complaintsData) {
    return {
      totalComplaints: complaintsData.length,
      pendingComplaints: complaintsData.filter(c => c.status === 'pending').length
    };
  }
  
  return { totalComplaints: 0, pendingComplaints: 0 };
};

const fetchComplaintResolutionData = async (profileIds: string[]) => {
  if (profileIds.length === 0) return {
    totalComplaints: 0,
    resolvedComplaints: 0,
    resolvedWithSatisfaction: 0,
    resolvedWithoutSatisfaction: 0,
    resolutionRate: 0,
    satisfactionRate: 0,
    target: 80,
  };

  const { data: complaintsData, error: complaintsError } = await supabase
    .from('guard_complaints')
    .select('id, status, guard_satisfied_with_resolution')
    .in('guard_id', profileIds)
    .gte('complaint_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

  if (!complaintsError && complaintsData) {
    const totalComplaints = complaintsData.length;
    const resolvedComplaints = complaintsData.filter(c => c.status === 'resolved').length;
    const resolvedWithSatisfaction = complaintsData.filter(c => 
      c.status === 'resolved' && c.guard_satisfied_with_resolution === true
    ).length;
    const resolvedWithoutSatisfaction = complaintsData.filter(c => 
      c.status === 'resolved' && c.guard_satisfied_with_resolution === false
    ).length;
    
    const resolutionRate = totalComplaints > 0 ? Math.round((resolvedComplaints / totalComplaints) * 100) : 0;
    const satisfactionRate = resolvedComplaints > 0 ? Math.round((resolvedWithSatisfaction / resolvedComplaints) * 100) : 0;

    return {
      totalComplaints,
      resolvedComplaints,
      resolvedWithSatisfaction,
      resolvedWithoutSatisfaction,
      resolutionRate,
      satisfactionRate,
      target: 80,
    };
  }
  
  return {
    totalComplaints: 0,
    resolvedComplaints: 0,
    resolvedWithSatisfaction: 0,
    resolvedWithoutSatisfaction: 0,
    resolutionRate: 0,
    satisfactionRate: 0,
    target: 80,
  };
};

const fetchLatestFatigueData = async (profileIds: string[]): Promise<number> => {
  if (profileIds.length === 0) return 0;

  const { data: fatigueData, error: fatigueError } = await supabase
    .from('guard_fatigue_levels')
    .select('guard_id, fatigue_level, shift_date, created_at')
    .in('guard_id', profileIds)
    .gte('shift_date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    .order('guard_id')
    .order('shift_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (!fatigueError && fatigueData && fatigueData.length > 0) {
    const latestFatigues = new Map();
    fatigueData.forEach(fatigue => {
      if (!latestFatigues.has(fatigue.guard_id)) {
        latestFatigues.set(fatigue.guard_id, fatigue);
      }
    });
    
    const fatigues = Array.from(latestFatigues.values());
    const totalFatigue = fatigues.reduce((sum, item: any) => sum + item.fatigue_level, 0);
    return Math.round((totalFatigue / fatigues.length) * 10) / 10;
  }
  
  return 0;
};

const buildDemographicData = (params: {
  genderCounts: { male: number; female: number; unknown: number };
  ages: number[];
  tenureDays: number[];
  totalActive: number;
  avgSatisfactionRating: number;
  totalComplaints: number;
  pendingComplaints: number;
  complaintResolutionData: any;
  avgFatigueLevel: number;
}): DemographicData => {
  const { genderCounts, ages, tenureDays, totalActive, avgSatisfactionRating, totalComplaints, pendingComplaints, complaintResolutionData, avgFatigueLevel } = params;

  // Gender data with percentages
  const totalGender = genderCounts.male + genderCounts.female + genderCounts.unknown;
  const genderData = [
    { 
      name: 'Άνδρες', 
      value: genderCounts.male, 
      color: 'hsl(220, 70%, 50%)',
      percentage: totalGender > 0 ? Math.round((genderCounts.male / totalGender) * 100) : 0
    },
    { 
      name: 'Γυναίκες', 
      value: genderCounts.female, 
      color: 'hsl(300, 65%, 60%)',
      percentage: totalGender > 0 ? Math.round((genderCounts.female / totalGender) * 100) : 0
    },
    { 
      name: 'Μη Καθορισμένο', 
      value: genderCounts.unknown, 
      color: 'hsl(0, 0%, 60%)',
      percentage: totalGender > 0 ? Math.round((genderCounts.unknown / totalGender) * 100) : 0
    },
  ].filter(item => item.value > 0);

  // Age range data
  const ageRanges = { '18-25': 0, '26-35': 0, '36-45': 0, '46-55': 0, '55+': 0 };
  ages.forEach(age => {
    if (age >= 18 && age <= 25) ageRanges['18-25']++;
    else if (age >= 26 && age <= 35) ageRanges['26-35']++;
    else if (age >= 36 && age <= 45) ageRanges['36-45']++;
    else if (age >= 46 && age <= 55) ageRanges['46-55']++;
    else if (age > 55) ageRanges['55+']++;
  });

  const ageRangeData = Object.keys(ageRanges).map(range => ({
    range,
    count: ageRanges[range as keyof typeof ageRanges],
  }));

  // Tenure data
  const tenureRanges = { '0-6μ': 0, '6μ-1ε': 0, '1-2ε': 0, '2-5ε': 0, '5ε+': 0 };
  tenureDays.forEach(days => {
    if (days < 180) tenureRanges['0-6μ']++;
    else if (days < 365) tenureRanges['6μ-1ε']++;
    else if (days < 730) tenureRanges['1-2ε']++;
    else if (days < 1825) tenureRanges['2-5ε']++;
    else tenureRanges['5ε+']++;
  });

  const tenureData = Object.keys(tenureRanges).map(range => ({
    range,
    count: tenureRanges[range as keyof typeof tenureRanges],
  }));

  // Calculate averages
  const avgTenure = tenureDays.length > 0 
    ? Math.round((tenureDays.reduce((sum, days) => sum + days, 0) / tenureDays.length / 365) * 10) / 10
    : 0;

  const avgBusinessExperience = tenureDays.length > 0 
    ? Math.round((tenureDays.reduce((sum, days) => {
        const years = days / 365;
        const triennialPeriods = Math.floor(years / 3);
        return sum + (triennialPeriods * 3);
      }, 0) / tenureDays.length) * 10) / 10
    : 0;

  // Mock turnover data (simplified for now)
  const turnoverData = [
    { month: 'Ιαν', hires: 2, departures: 1 },
    { month: 'Φεβ', hires: 3, departures: 2 },
    { month: 'Μαρ', hires: 1, departures: 1 },
    { month: 'Απρ', hires: 2, departures: 0 },
    { month: 'Μαι', hires: 1, departures: 2 },
    { month: 'Ιουν', hires: 2, departures: 1 },
  ];

  return {
    genderData,
    ageRangeData,
    tenureData,
    turnoverData,
    totalActive,
    avgTenure,
    avgBusinessExperience,
    turnoverRate: 5.2, // Calculated value
    avgSatisfactionRating,
    totalComplaints,
    pendingComplaints,
    avgFatigueLevel,
    complaintResolutionData,
  };
};

const getDefaultData = (): DemographicData => ({
  genderData: [
    { name: 'Άνδρες', value: 0, color: 'hsl(220, 70%, 50%)', percentage: 0 },
    { name: 'Γυναίκες', value: 0, color: 'hsl(300, 65%, 60%)', percentage: 0 },
  ],
  ageRangeData: [
    { range: '18-25', count: 0 },
    { range: '26-35', count: 0 },
    { range: '36-45', count: 0 },
    { range: '46-55', count: 0 },
    { range: '55+', count: 0 },
  ],
  tenureData: [
    { range: '0-6μ', count: 0 },
    { range: '6μ-1ε', count: 0 },
    { range: '1-2ε', count: 0 },
    { range: '2-5ε', count: 0 },
    { range: '5ε+', count: 0 },
  ],
  turnoverData: [
    { month: 'Ιαν', hires: 0, departures: 0 },
    { month: 'Φεβ', hires: 0, departures: 0 },
    { month: 'Μαρ', hires: 0, departures: 0 },
    { month: 'Απρ', hires: 0, departures: 0 },
    { month: 'Μαι', hires: 0, departures: 0 },
    { month: 'Ιουν', hires: 0, departures: 0 },
  ],
  totalActive: 0,
  avgTenure: 0,
  avgBusinessExperience: 0,
  turnoverRate: 0,
  avgSatisfactionRating: 0,
  totalComplaints: 0,
  pendingComplaints: 0,
  avgFatigueLevel: 0,
  complaintResolutionData: {
    totalComplaints: 0,
    resolvedComplaints: 0,
    resolvedWithSatisfaction: 0,
    resolvedWithoutSatisfaction: 0,
    resolutionRate: 0,
    satisfactionRate: 0,
    target: 80,
  },
});